import React, { useEffect, useState } from 'react';
import { keyframes } from '@emotion/react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import agent from '../../agent';
import CommonLoader from '../Loaders/CommonLoader';
import ShakeAndFlipImage from './ShakeAndFlipImage';
import useTranslation from '../../customHooks/translations';
import Countdown from '../Countdown/CountdownComponent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER } from '../../constants/theme';

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 ${DEFAULT_THEME_COLOR}44; }
  50%       { box-shadow: 0 0 0 24px ${DEFAULT_THEME_COLOR}00; }
`;

const ShakeItView = (props) => {
  const translation = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 992 });

  const [isShaking, setIsShaking] = useState(false);
  const [shaked, setShaked] = useState(false);
  const [alreadyShaked, setAlreadyShaked] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAbleToShake, setIsAbleToShake] = useState(false);
  const [shakePrice, setShakePrice] = useState(null);
  const [nextShake, setNextShake] = useState(null);

  const paddingLeft = (isDesktop && props.leftNavigationOpen) ? 300 : (isDesktop && !props.leftNavigationOpen ? 75 : 0);

  useEffect(() => {
    agent.Shake.check().then(res => {
      setIsAbleToShake(res.isAble);
      setShakePrice(res.shakePrice);
      setNextShake(res.nextShake);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (shaked && !alreadyShaked) {
      agent.Shake.shake().then(res => {
        setShakePrice(res.shakePrice);
        setAlreadyShaked(true);
      });
    }
  }, [shaked]);

  useEffect(() => {
    const SHAKE_THRESHOLD = 15;
    let lastX = 0, lastY = 0, lastZ = 0;
    let shakeTimeout = null;

    const handleDeviceMotion = (event) => {
      if (!event.accelerationIncludingGravity) return;
      const { x, y, z } = event.accelerationIncludingGravity;
      if (!x || !y || !z) return;
      const mag = Math.sqrt((x - lastX) ** 2 + (y - lastY) ** 2 + (z - lastZ) ** 2);
      if (mag > SHAKE_THRESHOLD) {
        setIsShaking(true);
        setShaked(true);
        if (shakeTimeout) clearTimeout(shakeTimeout);
        shakeTimeout = setTimeout(() => setIsShaking(false), 1000);
      }
      lastX = x; lastY = y; lastZ = z;
    };

    const init = async () => {
      if (DeviceMotionEvent && typeof DeviceMotionEvent.requestPermission === 'function') {
        try {
          const state = await DeviceMotionEvent.requestPermission();
          if (state === 'granted') {
            setPermissionGranted(true);
            window.addEventListener('devicemotion', handleDeviceMotion);
          } else {
            setErrorMessage('Permission to access device motion was denied.');
          }
        } catch {
          setErrorMessage('Error requesting device motion permission.');
        }
      } else {
        setPermissionGranted(true);
        window.addEventListener('devicemotion', handleDeviceMotion);
      }
    };

    init();
    return () => {
      window.removeEventListener('devicemotion', handleDeviceMotion);
      if (shakeTimeout) clearTimeout(shakeTimeout);
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)',
        paddingLeft: `${paddingLeft}px`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 1.5,
          background: 'rgba(13,27,42,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          size="small"
          sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white' } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: '1.3rem', lineHeight: 1 }}>🤚</Typography>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
          {translation.settings.shakeIt}
        </Typography>
      </Box>

      {/* ── Content ── */}
      {isLoading ? (
        <CommonLoader />
      ) : isAbleToShake ? (
        /* ── Can shake ── */
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 6,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: 'white', fontWeight: 800, mb: 1, fontSize: { xs: '1.75rem', md: '2.25rem' } }}
          >
            {translation.shake.shakeToWin}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', mb: 5, fontSize: '0.95rem' }}>
            {translation.shake.permissions}
          </Typography>

          {errorMessage && (
            <Box
              sx={{
                mb: 4,
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                background: 'rgba(248,113,113,0.1)',
                border: '1px solid rgba(248,113,113,0.3)',
              }}
            >
              <Typography sx={{ color: '#f87171', fontSize: '0.875rem' }}>{errorMessage}</Typography>
            </Box>
          )}

          {permissionGranted ? (
            <ShakeAndFlipImage price={shakePrice?.description} isShaking={isShaking} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <PhoneAndroidIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.15)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', maxWidth: 280 }}>
                {translation.shake.permissions}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Already shaked / prize ── */
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 6,
            textAlign: 'center',
          }}
        >
          {shakePrice ? (
            <>
              {/* Prize card */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 420,
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${DEFAULT_THEME_COLOR}44`,
                  borderRadius: 4,
                  p: 4,
                  mb: 4,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(circle at 50% 0%, ${DEFAULT_THEME_COLOR}18 0%, transparent 70%)`,
                    pointerEvents: 'none',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: `${DEFAULT_THEME_COLOR}22`,
                    border: `2px solid ${DEFAULT_THEME_COLOR}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2.5,
                    animation: `${pulseGlow} 2s ease-in-out infinite`,
                  }}
                >
                  <EmojiEventsIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 32 }} />
                </Box>

                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.75 }}>
                  {translation.shake.win}
                </Typography>
                <Typography
                  sx={{
                    color: 'white',
                    fontWeight: 800,
                    fontSize: { xs: '1.4rem', md: '1.75rem' },
                    background: DEFAULT_GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 3,
                  }}
                >
                  {shakePrice?.description}
                </Typography>

                {/* Countdown */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)' }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem' }}>
                    {translation.shake.onlyOnePerDay}
                  </Typography>
                  <Box sx={{ '& p': { color: DEFAULT_THEME_COLOR, fontWeight: 700, fontSize: '0.88rem', m: 0 } }}>
                    <Countdown date={nextShake} />
                  </Box>
                </Box>
              </Box>

              {/* Shop CTA */}
              <Button
                variant="contained"
                size="large"
                startIcon={<StorefrontIcon />}
                onClick={() => window.open('http://shop.freak-land.com/', '_blank')}
                sx={{
                  background: DEFAULT_GRADIENT,
                  color: 'white',
                  fontWeight: 700,
                  px: 4,
                  borderRadius: 3,
                  mb: 3,
                  '&:hover': { background: DEFAULT_GRADIENT_HOVER },
                }}
              >
                {translation.shake.visitShop}
              </Button>

              <Box
                component="a"
                href="http://shop.freak-land.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'block', borderRadius: 3, overflow: 'hidden', border: `1px solid ${DEFAULT_THEME_COLOR}33` }}
              >
                <Box
                  component="img"
                  src="./freakland-shop-v2.png"
                  alt="Freakland Shop"
                  sx={{ width: 180, height: 180, display: 'block', objectFit: 'cover' }}
                />
              </Box>
            </>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = state => ({
  leftNavigationOpen: state.common.leftNavigationOpen,
});

export default connect(mapStateToProps)(ShakeItView);
